/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  computed,
  forwardRef,
  input,
  ViewChild,
  ElementRef,
  signal,
  output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import {
  faXmark,
  faChevronDown,
  faCircleXmark,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ButtonComponent } from '@shared/components/button';
import { AcceptedFileTypes, FileListItem } from './types';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-file-container',
  templateUrl: './file-container.component.html',
  styleUrls: ['./file-container.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileContainerComponent),
      multi: true,
    },
  ],
  imports: [MatTooltipModule, FontAwesomeModule, ButtonComponent],
})
export class FileContainerComponent implements ControlValueAccessor {
  label = input('');
  fileList = input<FileListItem[] | null>(null);
  formControlName = input.required<string>();
  public isDeleteDisable = input(false);

  deleteFile = output<FileListItem>();
  viewFile = output<FileListItem>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  isListOpen = signal(false);

  placeholder = input('Choose a file');
  faXmark = faXmark;
  faChevronDown = faChevronDown;
  faCircleXmark = faCircleXmark;
  faEye = faEye;
  acceptedFileTypes = input.required<AcceptedFileTypes>();

  accept = computed(() => {
    const types = this.acceptedFileTypes();
    return Object.entries(types)
      .filter(([_, value]) => value)
      .map(([key]) => `.${key}`)
      .join(',');
  });

  private _selectedFiles: File | null = null;
  private _isDisabledState = false;

  fileInputId = computed(() => `file_input_${this.formControlName()}`);

  get hasFiles(): boolean {
    return this._selectedFiles !== null;
  }

  get fileDisplayName(): string {
    if (!this._selectedFiles) return '';

    if (this._selectedFiles instanceof FileList) {
      if (this._selectedFiles.length === 1) {
        return this._selectedFiles[0].name;
      }
      return `${this._selectedFiles.length} files selected`;
    }

    return this._selectedFiles.name;
  }

  get isDisabled() {
    return this._isDisabledState;
  }

  // ControlValueAccessor callback functions
  private onChange = (value: File | null) => {};
  private onTouched = () => {};

  onButtonClick(): void {
    this.fileInput.nativeElement.click();
  }

  onListOpenClick(): void {
    this.isListOpen.set(!this.isListOpen());
  }

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (files && files.length > 0) {
      this._selectedFiles = files[0];
      this.onChange(this._selectedFiles);
    } else {
      this._selectedFiles = null;
      this.onChange(null);
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  clearFiles(): void {
    this._selectedFiles = null;
    this.onChange(null);

    // Reset the file input
    const fileInput = document.getElementById(
      this.fileInputId(),
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: File | null): void {
    this._selectedFiles = value || null;
  }

  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabledState = isDisabled;
  }
}
